











































import { IresDevice } from "@/data/models/IresDevice";
import {
  DryingPhaseInfo,
  getEndDateOfProgramChain,
  getDryingPhaseInfoOfProgramChain,
  ProgramChain,
} from "@/data/models/ProgramChain";
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { ComBoxSampleInfo } from "@/data/models/SampleInfo";
import {
  ComBoxSensorData,
  ComBoxSensorType,
  isIndexOfRoomSensor,
} from "@/data/models/ComBoxSensorData";
import {
  ComBoxHumidityUnit,
  getComBoxHumidityUnitFormatRes,
} from "@/data/models/ComBoxHumidityUnit";
import { getNumberDaysBetween } from "@/utils/DateHelper";
import { PowerConsumptionValues } from "@/utils/DryingProgressGraphDataExtractor";
import DryingInfoTableComponent, {
  DryingInfoTable,
  DryingInfoTableRow,
} from "../DryingInfoTable.vue";
import { isPostDryingProgram, isRoomDryingProgram } from "@/data/models/ComBox";
import { RunOverview } from "@/data/models/RunOverview";

const PhaseQualityAttributeNames = [
  "phaseLabel",
  "phaseDurationInDays",

  "deltaRelativeHumidity",
  "deltaMixingRatio",

  "progressRelativeHumidity",
  "progressMixingRatio",

  "minimumPowerConsumption",
  "maximumPowerConsumption"
];

type PhaseQualityAttributes = typeof PhaseQualityAttributeNames[number];
type PhaseQualityInfo = {
  [Attribute in PhaseQualityAttributes]: string;
};

@Component({
  components: {
    "drying-info-table": DryingInfoTableComponent,
  },
})
export default class PhaseQualityOverviewComponent extends Vue {
  @Prop()
  powerConsumptionValues!: PowerConsumptionValues | null;

  @Prop()
  iresDevice!: IresDevice;

  @Prop({ default: false })
  showCompactVersion!: boolean;

  @Prop()
  programChain!: ProgramChain;

  @Watch("powerConsumptionValues")
  onPowerConsumptionValuesChanged() {
    this.processProgramChain();
  }

  rowLabels = new Map<PhaseQualityAttributes, string>([
    ["phaseDurationInDays", "duration"],

    ["deltaRelativeHumidity", "delta"],
    ["deltaMixingRatio", "delta"],

    ["progressRelativeHumidity", "progress"],
    ["progressMixingRatio", "progress"],

    ["maximumPowerConsumption", "maximumPowerConsumption"],
    ["minimumPowerConsumption", "minimumPowerConsumption"],
  ]);

  rowUnits = new Map<PhaseQualityAttributes, string>([
    ["phaseDurationInDays", "days"],

    ["deltaRelativeHumidity", "relativeHumidity"],
    ["deltaMixingRatio", "mixingRatio"],

    ["progressRelativeHumidity", "progressRelativeHumidity"],
    ["progressMixingRatio", "progressMixingRatio"],

    ["maximumPowerConsumption", "watt"],
    ["minimumPowerConsumption", "watt"],
  ]);

  mounted() {
    this.processProgramChain();
  }

  phaseQualityAttributeNames = PhaseQualityAttributeNames.filter(
    (value) => value != "phaseLabel"
  );

  phaseQualityInfo: PhaseQualityInfo[] = [];

  get showInfo() {
    return this.phaseQualityInfo.length > 0;
  }

  get headerRow() {
    return [
      this.$t("rateDrying.qualityPhase.title").toString(),
      ...this.phaseQualityInfo.map((info) => info.phaseLabel),
    ];
  }

  getAttributeRow(attribute: PhaseQualityAttributes) {
    return [
      this.$t(
        `rateDrying.qualityPhase.${this.rowLabels.get(attribute)}`
      ).toString(),
      this.$t(
        `rateDrying.qualityPhase.${this.rowUnits.get(attribute)}`
      ).toString(),
      ...this.phaseQualityInfo.map((info) => info[attribute]),
    ];
  }

  calculatePhaseQualityInfo(
    previousPhaseInfo: DryingPhaseInfo,
    info: DryingPhaseInfo,
    includeRoomSensorInDeltaCalculation: boolean,
    forcedLabel?: string,
    roundDays?: boolean
  ) {
    const deltaRelativeHumidity = this.getMaxDelta(
      previousPhaseInfo.phaseEndSample,
      info.phaseEndSample,
      includeRoomSensorInDeltaCalculation,
      (sensorData: ComBoxSensorData) => sensorData.relativeHumidity
    );

    const deltaMixingRatio = this.getMaxDelta(
      previousPhaseInfo.phaseEndSample,
      info.phaseEndSample,
      includeRoomSensorInDeltaCalculation,
      (sensorData: ComBoxSensorData) => sensorData.mixingRatio
    );

    const maximumPowerConsumption = info.phase === -1
      ? this.powerConsumptionValues?.globalMaximum
      : this.powerConsumptionValues?.localMaximum[info.phase - 1];
    const minimumPowerConsumption = info.phase === -1
      ? this.powerConsumptionValues?.globalMinimum
      : this.powerConsumptionValues?.localMinimum[info.phase - 1];

    const timeDiff =
      info.phaseEndDate.getTime() - previousPhaseInfo.phaseEndDate.getTime();
    const timeDiffInDays = timeDiff / (24 * 60 * 60 * 1000);

    const roundedTimeDiffInDays = getNumberDaysBetween(
      info.phaseEndDate,
      previousPhaseInfo.phaseEndDate,
      roundDays
    );

    let progressRelativeHumidity: number | undefined = undefined;
    let progressMixingRatio: number | undefined = undefined;

    if (timeDiffInDays >= 0.5) {
      if (deltaRelativeHumidity !== undefined) {
        progressRelativeHumidity = deltaRelativeHumidity / timeDiffInDays;
      }

      if (deltaMixingRatio !== undefined) {
        progressMixingRatio = deltaMixingRatio / timeDiffInDays;
      }
    }

    return {
      phaseLabel:
        forcedLabel ??
        this.$t("rateDrying.formatPhase", [info.phase]).toString(),
      phaseDurationInDays: roundedTimeDiffInDays.toString(),
      deltaRelativeHumidity: !deltaRelativeHumidity
        ? ""
        : this.$t(
            getComBoxHumidityUnitFormatRes(
              ComBoxHumidityUnit.RELATIVE_HUMIDITY
            ),
            [
              deltaRelativeHumidity.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              }),
            ]
          ).toString(),

      deltaMixingRatio: !deltaMixingRatio
        ? ""
        : this.$t(
            getComBoxHumidityUnitFormatRes(ComBoxHumidityUnit.MIXING_RATIO),
            [
              deltaMixingRatio.toLocaleString(undefined, {
                maximumFractionDigits: 1,
              }),
            ]
          ).toString(),
      progressMixingRatio: !progressMixingRatio
        ? ""
        : progressMixingRatio.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          }),
      progressRelativeHumidity: !progressRelativeHumidity
        ? ""
        : progressRelativeHumidity.toLocaleString(undefined, {
            maximumFractionDigits: 1,
          }),
      maximumPowerConsumption: !maximumPowerConsumption && maximumPowerConsumption !== 0
        ? ""
        : maximumPowerConsumption.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
      minimumPowerConsumption: !minimumPowerConsumption && minimumPowerConsumption !== 0
        ? ""
        : minimumPowerConsumption.toLocaleString(undefined, {
            maximumFractionDigits: 2,
          }),
    };
  }

  includeRoomSensorInDeltaCalculation(runOverview: RunOverview) {
    if (isRoomDryingProgram(runOverview.programNumber)) {
      return true;
    }

    return !runOverview.sensorConfig.some(
      (sensorType) => sensorType === ComBoxSensorType.TELAIRE
    );
  }

  processProgramChain() {
    const dryingPhaseInfo = getDryingPhaseInfoOfProgramChain(
      this.programChain,
      true
    );

    if (dryingPhaseInfo.length === 0) {
      return;
    }

    const firstRun = this.programChain.runs[0];
    const firstSample = firstRun?.samples[0];

    const lastRun = this.programChain.runs[this.programChain.runs.length - 1];
    const lastSample = lastRun?.samples[lastRun.samples.length - 1];

    if (!firstSample || !lastSample) {
      return;
    }

    const result: PhaseQualityInfo[] = [];

    const chainStartInfo = {
      phase: -1,
      phaseEndDay: -1,
      phaseEndSample: firstSample as ComBoxSampleInfo,
      phaseStartDate: this.programChain.startDate.toDate(),
      phaseEndDate: this.programChain.startDate.toDate(),
    };

    const chainEndEndInfo: DryingPhaseInfo = {
      phase: -1,
      phaseEndDay: -1,
      phaseEndSample: lastSample as ComBoxSampleInfo,
      phaseStartDate: this.programChain.startDate.toDate(),
      phaseEndDate: getEndDateOfProgramChain(this.programChain).toDate(),
    };

    let previousPhaseInfo: DryingPhaseInfo = chainStartInfo;

    const includeRoomSensorInDeltaCalculation = this.includeRoomSensorInDeltaCalculation(
      firstRun
    );

    result.push(
      this.calculatePhaseQualityInfo(
        chainStartInfo,
        chainEndEndInfo,
        includeRoomSensorInDeltaCalculation,
        this.$t("rateDrying.qualityPhase.overall").toString(),
        false
      )
    );

    dryingPhaseInfo.forEach((info) => {
      result.push(
        this.calculatePhaseQualityInfo(
          previousPhaseInfo,
          info,
          includeRoomSensorInDeltaCalculation,
          undefined,
          true
        )
      );
      previousPhaseInfo = info;
    });

    const firstPostDryingRun = this.programChain.runs.find((run) =>
      isPostDryingProgram(run.programNumber)
    );

    if (firstPostDryingRun) {
      const firstPostDryingSample = firstPostDryingRun.samples[0];

      const postDryingInfo: DryingPhaseInfo = {
        phase: -1,
        phaseEndDay: -1,
        phaseEndSample: firstPostDryingSample as ComBoxSampleInfo,
        phaseStartDate: firstPostDryingRun.startDate.toDate(),
        phaseEndDate: firstPostDryingRun.startDate.toDate(),
      };

      result.push(
        this.calculatePhaseQualityInfo(
          postDryingInfo,
          chainEndEndInfo,
          includeRoomSensorInDeltaCalculation,
          this.$t("rateDrying.postDryingRun").toString()
        )
      );
    }

    this.phaseQualityInfo = result;
  }

  getMaxDelta(
    previous: ComBoxSampleInfo,
    now: ComBoxSampleInfo,
    includeRoomSensorInDeltaCalculation: boolean,
    valueCallback: (sensorData: ComBoxSensorData) => number | undefined
  ) {
    let maxHumidityNow = Number.MIN_SAFE_INTEGER;
    let indexOfSensorWithMaxHumidity = -1;

    for (let i = 0; i < now.sensorData.length; i++) {
      if (includeRoomSensorInDeltaCalculation || !isIndexOfRoomSensor(i)) {
        const senorDataNow = now.sensorData[i];

        if (senorDataNow) {
          const valueNow = valueCallback(senorDataNow);

          if (valueNow !== undefined && maxHumidityNow < valueNow) {
            maxHumidityNow = valueNow;
            indexOfSensorWithMaxHumidity = i;
          }
        }
      }
    }

    if (indexOfSensorWithMaxHumidity !== -1) {
      const senorDataPrevious =
        previous.sensorData[indexOfSensorWithMaxHumidity];

      if (senorDataPrevious) {
        const valuePrevious = valueCallback(senorDataPrevious);

        if (valuePrevious !== undefined) {
          return maxHumidityNow - valuePrevious;
        }
      }
    }

    return undefined;
  }

  get dryingInfoTable() {
    const rows: DryingInfoTableRow[] = [];

    const headerRow = this.headerRow;

    rows.push({
      bold: true,
      rowLabels: headerRow.slice(0, 1),
      rowValues: headerRow.slice(1),
    });

    this.phaseQualityAttributeNames.forEach((attributeName) => {
      const attributeRow = this.getAttributeRow(attributeName);

      rows.push({
        rowLabels: attributeRow.slice(0, 2),
        rowValues: attributeRow.slice(2),
      });
    });

    const table: DryingInfoTable = {
      rows: rows,
    };

    return table;
  }
}
